import { X, Image as ImageIcon, Pencil } from 'lucide-react'
import { LearningContentText } from '@quebon/shared-learning/components/common/LearningContentText'
import type { QuetureContentResponse } from '@/api/contentInspection'

type Buildup = NonNullable<QuetureContentResponse['buildup']>
type BuildupItem = Buildup[number]['items'][number]

interface Props {
  open: boolean
  onClose: () => void
  onEdit: () => void
  buildup: Buildup | null
  lessonTitle?: string
}

function Field({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null
  return (
    <div className="grid grid-cols-[80px_1fr] gap-2 text-sm">
      <div className="text-xs font-semibold text-slate-500">{label}</div>
      <div className="text-slate-700 whitespace-pre-wrap">
        <LearningContentText text={value} />
      </div>
    </div>
  )
}

function ImageGallery({ label, urls }: { label: string; urls?: string[] }) {
  if (!urls?.length) return null
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1 text-xs font-semibold text-slate-500">
        <ImageIcon className="w-3 h-3" /> {label} ({urls.length})
      </div>
      <div className="flex flex-wrap gap-2">
        {urls.map((url, i) => (
          <a key={i} href={url} target="_blank" rel="noreferrer"
             className="block w-20 h-20 rounded border border-slate-200 overflow-hidden hover:border-indigo-400">
            <img src={url} alt={`${label} ${i + 1}`} className="w-full h-full object-cover" />
          </a>
        ))}
      </div>
    </div>
  )
}

function ItemCard({ item }: { item: BuildupItem }) {
  return (
    <div className="p-4 bg-white rounded-lg border border-slate-200 space-y-3">
      <div className="text-xs font-semibold text-indigo-600">#{item.number}</div>
      <div className="space-y-1.5">
        <Field label="발문" value={item.title} />
        <Field label="문제" value={item.problem} />
        <Field label="보기" value={item.options} />
        <Field label="정답" value={item.answer} />
        <Field label="해설" value={item.desc} />
        <Field label="정답 기준" value={item.answerCriteria} />
        <Field label="선생님 체크" value={item.teacherCheck} />
      </div>
      <ImageGallery label="문제 이미지" urls={item.probImageUrls} />
      <ImageGallery label="해설 이미지" urls={item.descImageUrls} />
      <ImageGallery label="문제 인쇄용" urls={item.probPrintImageUrls} />
      <ImageGallery label="해설 인쇄용" urls={item.descPrintImageUrls} />
    </div>
  )
}

export function BuildupDetailModal({ open, onClose, onEdit, buildup, lessonTitle }: Props) {
  if (!open) return null
  const steps = buildup ?? []
  const totalItems = steps.reduce((sum, s) => sum + s.items.length, 0)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        <header className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div>
            <h2 className="text-base font-semibold text-slate-900">
              빌드업 상세 {lessonTitle && <span className="text-slate-500 font-normal">— {lessonTitle}</span>}
            </h2>
            <p className="text-xs text-slate-500">{steps.length}스텝 · {totalItems}아이템</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onEdit}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded hover:bg-indigo-100"
            >
              <Pencil className="w-3 h-3" />
              수정
            </button>
            <button onClick={onClose} className="p-1 rounded hover:bg-slate-100">
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>
        </header>
        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {steps.length === 0 ? (
            <div className="py-10 text-sm text-center text-slate-400">데이터가 없습니다</div>
          ) : (
            steps.map((step, si) => (
              <section key={si} className="space-y-3">
                <div className="flex items-baseline gap-2">
                  <span className="px-2 py-0.5 text-xs font-mono rounded bg-indigo-100 text-indigo-700">{step.step}</span>
                  <h3 className="text-sm font-semibold text-slate-800">
                    <LearningContentText text={step.title} />
                  </h3>
                </div>
                <div className="space-y-3 pl-4 border-l-2 border-indigo-100">
                  {step.items.map((item, ii) => <ItemCard key={ii} item={item} />)}
                </div>
              </section>
            ))
          )}
        </div>
      </div>
    </div>
  )
}
